import { ReactNode } from 'react';
import { AnalyticsAuthSocialAccountType } from '@anytag/graphql';
import { checkIconImage } from '@anytag/shared-assets';
import { cn } from '@anytag/shared-utils';
import { Anchor, AnchorProps } from '../Anchor';
import { Tooltip } from '../Tooltip';
import { Icon, IconProps } from './Icon';

export enum SocialMediaIconType {
  XHS = 'XHS',
  EMAIL = 'EMAIL',
  TIKTOK = 'TIKTOK',
  DOUYIN = 'DOUYIN',
  TWITTER = 'TWITTER',
  THREADS = 'THREADS',
  YOUTUBE = 'YOUTUBE',
  FACEBOOK = 'FACEBOOK',
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK_PAGE = 'FACEBOOK_PAGE'
}

export interface CustomSocialIconProps extends AnchorProps {
  active?: boolean;
  help?: ReactNode;
  className?: string;
  isWarning?: boolean;
  isSignedUp?: boolean;
  size?: IconProps['size'];
  type: SocialMediaIconType | AnalyticsAuthSocialAccountType;
}

export const CustomSocialIcon = ({
  type,
  help,
  isWarning,
  isSignedUp,
  className,
  size = '16px',
  active = true,
  ...anchorProps
}: CustomSocialIconProps) => {
  // https://www.figma.com/design/4FcfqHTdafr6BTRxiTVoRr/AnyTag-Dashboard?node-id=32331-41870&m=dev
  // TODO: migrate to TW
  const { iconProps, activeBackground } = ((): {
    iconProps?: Pick<IconProps, 'icon' | 'size'>;
    activeBackground?: string;
  } => {
    switch (type) {
      case SocialMediaIconType.THREADS:
        return { iconProps: { icon: 'threads-transform' }, activeBackground: cn('bg-[#130B18]') };
      case SocialMediaIconType.EMAIL:
        return { iconProps: { icon: 'email' }, activeBackground: cn('bg-[#9EA0E0]') };
      case SocialMediaIconType.YOUTUBE:
        return { iconProps: { icon: 'youtube' }, activeBackground: cn('bg-[#FF0302]') };
      case SocialMediaIconType.TWITTER:
        return { iconProps: { icon: 'x-logo' }, activeBackground: cn('bg-[#000]') };
      case SocialMediaIconType.FACEBOOK:
      case SocialMediaIconType.FACEBOOK_PAGE:
        return { iconProps: { icon: 'facebook' }, activeBackground: cn('bg-[#1877f2]') };
      case SocialMediaIconType.TIKTOK:
        return {
          iconProps: { icon: active ? 'tiktok-transform' : 'tiktok' },
          activeBackground: cn('bg-[#010101]')
        };
      case SocialMediaIconType.DOUYIN:
        return {
          iconProps: { icon: active ? 'douyin-text-transform' : 'douyin-text', size: '22px' },
          activeBackground: cn('bg-[#130B18]')
        };
      case SocialMediaIconType.XHS:
        return { iconProps: { icon: 'xhs-text', size: '26px' }, activeBackground: cn('bg-[#FF2842]') };
      case SocialMediaIconType.INSTAGRAM:
        return {
          iconProps: { icon: 'instagram' },
          activeBackground: cn('bg-gradient-to-r from-[#EBB752] via-[#E83262] via-[#FF6641] to-[#CE3396]')
        };
      default:
        return { iconProps: undefined, activeBackground: undefined };
    }
  })();

  return iconProps ? (
    <Tooltip
      help={help}
      place="top"
      positionStrategy="fixed"
      className={cn(!anchorProps.to && !anchorProps.href ? 'pointer-events-none' : 'cursor-pointer')}
    >
      <Anchor
        variant="inherit"
        className={cn(
          'relative inline-flex flex-none items-center justify-center rounded-sm object-contain text-white',
          active ? activeBackground : 'bg-black-30',
          className
        )}
        {...anchorProps}
      >
        <Icon icon={iconProps?.icon || ''} size={iconProps?.size || size} variant="white" />
        {isSignedUp && (
          <img
            className="absolute top-[-4px] right-[-4px] box-border rounded-full border-base-white bg-white"
            width="16"
            height="16"
            src={checkIconImage}
            alt="check icon"
          />
        )}
        {isWarning && (
          <div className="absolute top-[-4px] right-[-4px] h-3.5 w-4 rounded-sm bg-attention-yellow text-center">
            <Icon icon="warning" size="10px" variant="white" className="absolute top-0.5 left-[3px]" />
          </div>
        )}
      </Anchor>
    </Tooltip>
  ) : null;
};
